// Port of mobile src/services/authService.ts, minus the anonymous-first
// model: on web, users must sign in with Google or Apple before using the
// app (product decision — no guest accounts in the browser).
import {
  GoogleAuthProvider,
  OAuthProvider,
  linkWithPopup,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import {auth} from '../firebase';

export const isAppleLinked = (user: User): boolean =>
  user.providerData.some(p => p.providerId === 'apple.com');

export const getAppleEmail = (user: User): string | null =>
  user.providerData.find(p => p.providerId === 'apple.com')?.email ?? null;

export const isGoogleLinked = (user: User): boolean =>
  user.providerData.some(p => p.providerId === 'google.com');

export const getGoogleEmail = (user: User): string | null =>
  user.providerData.find(p => p.providerId === 'google.com')?.email ?? null;

/**
 * Waits for the persisted auth state to restore. Resolves with the signed-in
 * user, or null when the visitor must go through the login screen.
 */
export const waitForAuthReady = (): Promise<User | null> =>
  new Promise(resolve => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      unsubscribe();
      resolve(user);
    });
  });

const googleProvider = () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({prompt: 'select_account'});
  return provider;
};

const appleProvider = () => {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return provider;
};

const providerFor = (name: 'apple' | 'google') =>
  name === 'apple' ? appleProvider() : googleProvider();

/**
 * Links the current (anonymous) account with the given provider so data
 * survives across devices. Throws `auth/credential-already-in-use` when the
 * provider account already backs another Firebase user — handle via merge.
 */
export const linkWithProvider = async (
  name: 'apple' | 'google',
): Promise<User> => {
  const current = auth.currentUser;
  if (!current) {
    throw new Error('No signed-in user to link');
  }
  const result = await linkWithPopup(current, providerFor(name));
  return result.user;
};

/**
 * Signs in with the provider directly — used to switch to an existing
 * account (abandons the current anonymous account).
 */
export const signInWithProvider = async (
  name: 'apple' | 'google',
): Promise<User> => {
  const result = await signInWithPopup(auth, providerFor(name));
  return result.user;
};

export const subscribeAuthState = (
  callback: (user: User | null) => void,
): (() => void) => onAuthStateChanged(auth, callback);

export const currentUid = (): string | null => auth.currentUser?.uid ?? null;

export const signOutForAccountSwitch = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (e) {
    console.warn('Sign out failed', e);
  }
};
